import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { usersApi } from '../lib/api';
import { Users, UserPlus, Trash2, Mail, Shield, Loader2, ShieldCheck, AlertTriangle, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const roleColor = {
  Director: 'bg-amber-100 text-amber-800',
  Admin: 'bg-violet-100 text-violet-800',
  'Sales Executive': 'bg-emerald-100 text-emerald-800',
  'Sales Manager': 'bg-sky-100 text-sky-800',
};

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'Sales Executive' });
  const [justCreated, setJustCreated] = useState(null); // { name, email, password } — persistent creds banner

  const isAdmin = user && (user.role === 'Director' || user.role === 'Admin');

  const load = async () => {
    setLoading(true);
    try {
      const data = await usersApi.list();
      setUsers(data);
    } catch (e) {
      const msg = e?.response?.status === 403
        ? 'Only Director or Admin can manage the team.'
        : 'Failed to load team members';
      toast.error(msg);
      setUsers([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

  const add = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email and password are required');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    // Trim + lowercase email so it exactly matches what the rep will type
    const clean = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.password.trim(),
      role: form.role,
    };
    setSaving(true);
    try {
      const created = await usersApi.create(clean);
      setUsers(prev => [created, ...prev]);
      setOpen(false);
      setJustCreated({ name: created.name, email: created.email, password: clean.password });
      setForm({ name: '', email: '', password: '', role: 'Sales Executive' });
      toast.success(`${created.name} added — share the credentials shown below`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add team member');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirm) return;
    try {
      await usersApi.remove(confirm.id);
      setUsers(prev => prev.filter(u => u.id !== confirm.id));
      toast.success(`${confirm.name} removed from team`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove team member');
    } finally {
      setConfirm(null);
    }
  };

  const resetPassword = async () => {
    if (!resetFor) return;
    if (resetPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResetSaving(true);
    try {
      await usersApi.update(resetFor.id, { password: resetPw });
      toast.success(`Password reset for ${resetFor.name}. Share it securely.`);
      setResetFor(null);
      setResetPw('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reset password');
    } finally { setResetSaving(false); }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center">
        <div className="inline-flex w-16 h-16 rounded-full bg-rose-100 items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-rose-600" />
        </div>
        <h2 className="text-2xl font-extrabold text-emerald-950">Restricted Area</h2>
        <p className="mt-2 text-slate-600">Only the Director or Admin role can manage the sales team. Please log in with director@inpacepowers.com to access this area.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-emerald-950 flex items-center gap-2">
            <Users className="w-7 h-7 text-emerald-700" /> Team Management
          </h1>
          <p className="text-slate-600 mt-1">{users.length} member{users.length !== 1 ? 's' : ''} • Add or remove sales staff</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white">
          <UserPlus className="w-4 h-4 mr-2" /> Add Team Member
        </Button>
      </div>

      {justCreated && (
        <Card className="p-5 border-2 border-emerald-300 bg-emerald-50" data-testid="just-created-banner">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="text-xs font-bold text-emerald-700 uppercase tracking-widest">Team member added — share these credentials</div>
              <div className="text-sm font-bold text-emerald-950 mt-2">{justCreated.name}</div>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div className="p-3 rounded-lg bg-white border border-emerald-200">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Login Email</div>
                  <div className="font-mono text-sm text-emerald-900 mt-1 break-all">{justCreated.email}</div>
                </div>
                <div className="p-3 rounded-lg bg-white border border-emerald-200">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Temporary Password</div>
                  <div className="font-mono text-sm text-emerald-900 mt-1 break-all">{justCreated.password}</div>
                </div>
              </div>
              <div className="text-[11px] text-emerald-700 mt-2">Share this over WhatsApp/SMS. The team member can change their password later from their profile.</div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `Inpace Power CRM login\nEmail: ${justCreated.email}\nPassword: ${justCreated.password}\nLogin at: ${window.location.origin}/login`
                  );
                  toast.success('Credentials copied to clipboard');
                }}
                data-testid="copy-creds-btn"
              >
                Copy
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setJustCreated(null)} data-testid="dismiss-creds-btn">Dismiss</Button>
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="py-16 flex items-center justify-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading team…
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map(u => (
            <Card key={u.id} className="p-5 border-emerald-100 hover:shadow-lg transition-all">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white font-bold flex items-center justify-center text-lg shrink-0">
                  {u.avatar || u.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-emerald-950 truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{u.email}</div>
                  <Badge className={`mt-2 ${roleColor[u.role] || 'bg-slate-100 text-slate-700'} hover:bg-inherit border-0 rounded-full text-[10px]`}>
                    <Shield className="w-3 h-3 mr-1" />{u.role}
                  </Badge>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-emerald-50 flex items-center justify-between">
                <span className="text-xs text-slate-500">Joined {(u.created_at || '').slice(0, 10)}</span>
                {u.id === user.id ? (
                  <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-0 text-[10px]">
                    <ShieldCheck className="w-3 h-3 mr-1" />You
                  </Badge>
                ) : (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-8 px-2"
                      title="Reset password"
                      onClick={() => { setResetFor(u); setResetPw(''); }}
                      data-testid={`reset-pw-${u.email}`}>
                      <KeyRound className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      title="Remove member"
                      onClick={() => setConfirm(u)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
          {users.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500">No team members yet.</div>
          )}
        </div>
      )}

      {/* Add user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader><DialogTitle className="text-emerald-950">Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1.5 block">Full Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya Nair" />
            </div>
            <div>
              <Label className="mb-1.5 block">Work Email</Label>
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" placeholder="priya@inpacepowers.com" />
            </div>
            <div>
              <Label className="mb-1.5 block">Temporary Password</Label>
              <Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} type="text" placeholder="Min 6 characters" />
              <div className="text-xs text-slate-500 mt-1">Share securely. They can change it later.</div>
            </div>
            <div>
              <Label className="mb-1.5 block">Role</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sales Executive">Sales Executive</SelectItem>
                  <SelectItem value="Sales Manager">Sales Manager</SelectItem>
                  <SelectItem value="Admin">Admin (full access)</SelectItem>
                  <SelectItem value="Director">Director (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={add} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm remove dialog */}
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirm?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revoke their access immediately. Their leads and proposals will remain in the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700 text-white">
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset password dialog (admin action) */}
      <Dialog open={!!resetFor} onOpenChange={(v) => !v && setResetFor(null)}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="text-emerald-950 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-orange-600" /> Reset password for {resetFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-slate-600">Set a new temporary password. Share it securely with <strong>{resetFor?.email}</strong> — they can change it later from their own account.</div>
            <div>
              <Label className="mb-1.5 block">New Password</Label>
              <Input type="text" value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="Min 6 characters" data-testid="admin-reset-password-input" autoFocus />
              <div className="text-xs text-slate-500 mt-1">Tip: use a memorable phrase like <code>Inpace@Fresh25</code>.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetFor(null)} disabled={resetSaving}>Cancel</Button>
            <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={resetPassword} disabled={resetSaving} data-testid="admin-reset-password-submit">
              {resetSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
